import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shared Change Order Edit Dialog Component
 * Used by both Project Detail page and Change Order Log page
 * 
 * @param {boolean} open - Whether dialog is open
 * @param {function} onOpenChange - Callback when dialog open state changes
 * @param {object} changeOrder - Change order data to edit (null for create mode)
 * @param {object} project - Project context
 * @param {function} onSuccess - Callback after successful save
 * @param {boolean} editMode - Whether in edit mode (true) or create mode (false)
 * @param {function} canViewCosts - Function to check if user can view costs
 */
export default function ChangeOrderEditDialog({
  open,
  onOpenChange,
  changeOrder,
  project,
  onSuccess,
  editMode = false,
  canViewCosts = () => true
}) {
  const submitActionRef = useRef('update');
  
  const [formData, setFormData] = useState({
    id: "",
    change_order_number: "",
    version: "1.0",
    parent_change_order_id: null,
    type: "Detailing",
    line_items: [
      {
        id: crypto.randomUUID(),
        description: "",
        reference_drawing: "",
        element_name: "",
        element_quantity: "",
        action: "Addition",
        hours: "",
        cost: ""
      }
    ],
    time_impact: 0,
    cost_impact: 0,
    status: "pending",
    approved_hours: 0,
    approved_cost: 0,
    approved_date: "",
    branch: "",
    hyperlink: "",
    remarks: "",
    work_completion_status: "yet_to_start",
    created_at: null,
    is_invoiced: false,
    is_locked: false
  });

  const [duplicateWarning, setDuplicateWarning] = useState("");

  // Load change order data when dialog opens
  useEffect(() => {
    if (open && changeOrder && editMode) {
      setFormData({
        id: changeOrder.id || "",
        change_order_number: changeOrder.change_order_number || "",
        version: changeOrder.version || "1.0",
        parent_change_order_id: changeOrder.parent_change_order_id || null,
        type: changeOrder.type || "Detailing",
        line_items: changeOrder.line_items?.length > 0 ? changeOrder.line_items.map(item => ({
          id: item.id || crypto.randomUUID(),
          description: item.description || "",
          reference_drawing: item.reference_drawing || "",
          element_name: item.element_name || "",
          element_quantity: item.element_quantity || "",
          action: item.action || "Addition",
          hours: item.hours || "",
          cost: item.cost || ""
        })) : [{
          id: crypto.randomUUID(),
          description: "",
          reference_drawing: "",
          element_name: "",
          element_quantity: "",
          action: "Addition",
          hours: "",
          cost: ""
        }],
        time_impact: changeOrder.time_impact || 0,
        cost_impact: changeOrder.cost_impact || 0,
        status: changeOrder.status || "pending",
        approved_hours: changeOrder.approved_hours || 0,
        approved_cost: changeOrder.approved_cost || 0,
        approved_date: changeOrder.approved_date || "",
        branch: changeOrder.branch || "",
        hyperlink: changeOrder.hyperlink || "",
        remarks: changeOrder.remarks || "",
        work_completion_status: changeOrder.work_completion_status || "yet_to_start",
        created_at: changeOrder.created_at || null,
        is_invoiced: changeOrder.is_invoiced || false,
        is_locked: changeOrder.is_locked || false
      });
    } else if (open && !editMode) {
      // Reset for create mode
      setFormData({
        id: "",
        change_order_number: "",
        version: "1.0",
        parent_change_order_id: null,
        type: "Detailing",
        line_items: [
          {
            id: crypto.randomUUID(),
            description: "",
            reference_drawing: "",
            element_name: "",
            element_quantity: "",
            action: "Addition",
            hours: "",
            cost: ""
          }
        ],
        time_impact: 0,
        cost_impact: 0,
        status: "pending",
        approved_hours: 0,
        approved_cost: 0,
        approved_date: "",
        branch: "",
        hyperlink: "",
        remarks: "",
        work_completion_status: "yet_to_start",
        created_at: null,
        is_invoiced: false,
        is_locked: false
      });
      setDuplicateWarning("");
    }
  }, [open, changeOrder, editMode]);

  const addLineItem = () => {
    const newLineItem = {
      id: crypto.randomUUID(),
      description: "",
      reference_drawing: "",
      element_name: "",
      element_quantity: "",
      action: "Addition",
      hours: "",
      cost: ""
    };
    setFormData({
      ...formData,
      line_items: [...formData.line_items, newLineItem]
    });
  };

  const removeLineItem = (lineItemId) => {
    if (formData.line_items.length <= 1) {
      toast.error("At least one line item must remain");
      return;
    }
    setFormData({
      ...formData,
      line_items: formData.line_items.filter(item => item.id !== lineItemId)
    });
  };

  const updateLineItem = (lineItemId, field, value) => {
    const updatedLineItems = formData.line_items.map(item => {
      if (item.id === lineItemId) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setFormData({
      ...formData,
      line_items: updatedLineItems
    });
    // Recalculate totals
    calculateTotals(updatedLineItems);
  };

  const calculateTotals = (lineItems) => {
    const totalHours = lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.hours) || 0);
    }, 0);
    const totalCost = lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.cost) || 0);
    }, 0);
    
    setFormData(prev => ({
      ...prev,
      time_impact: totalHours,
      cost_impact: totalCost
    }));
  };

  const checkDuplicateCoNumber = (coNumber) => {
    if (!project || editMode) return; // Skip check in edit mode

    if (coNumber.trim()) {
      const existingNumbers = project.change_orders
        ?.map(co => co.change_order_number.trim()) || [];
      
      if (existingNumbers.includes(coNumber.trim())) {
        setDuplicateWarning(`⚠️ Warning: "${coNumber}" already exists in this project.`);
      } else {
        setDuplicateWarning("");
      }
    } else {
      setDuplicateWarning("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if CO is locked
    if (formData.is_invoiced) {
      toast.error("Cannot edit a change order that has been invoiced");
      return;
    }
    if (formData.is_locked) {
      toast.error("Cannot edit a change order that is affecting timesheet approvals");
      return;
    }

    // Validate branch for multi-branch projects
    const isMultiBranch = project && Array.isArray(project.branch) && project.branch.length > 1;
    if (isMultiBranch && !formData.branch) {
      toast.error("Please select a branch for this multi-branch project");
      return;
    }

    // Check for duplicates in create mode
    if (!editMode) {
      const coNumber = formData.change_order_number.trim();
      const existingNumbers = project.change_orders
        ?.map(co => co.change_order_number.trim()) || [];
      
      if (existingNumbers.includes(coNumber)) {
        toast.error(`Change order number "${coNumber}" already exists. Please use a different number.`);
        return;
      }
    }

    try {
      const token = localStorage.getItem("token");
      
      // Process line items
      const processedLineItems = formData.line_items.map(item => ({
        ...item,
        hours: parseFloat(item.hours) || 0,
        cost: parseFloat(item.cost) || 0
      }));

      const isUpdating = editMode && formData.id && formData.id.trim() !== "";
      const isCreatingNewVersion = submitActionRef.current === 'newVersion';

      if (isUpdating && !isCreatingNewVersion) {
        // Update existing version
        const updatePayload = {
          id: formData.id,
          change_order_number: formData.change_order_number,
          version: formData.version,
          parent_change_order_id: formData.parent_change_order_id,
          type: formData.type,
          line_items: processedLineItems,
          time_impact: parseFloat(formData.time_impact) || 0,
          cost_impact: parseFloat(formData.cost_impact) || 0,
          status: formData.status,
          approved_hours: parseFloat(formData.approved_hours) || 0,
          approved_cost: parseFloat(formData.approved_cost) || 0,
          approved_date: formData.status === "approved" ? (formData.approved_date || null) : null,
          branch: formData.branch || null,
          hyperlink: formData.hyperlink || null,
          remarks: formData.remarks || null,
          created_at: formData.created_at
        };

        await axios.put(
          `${API}/projects/${project.id}/change-orders/${formData.id}`,
          updatePayload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Change order updated!");
      } else if (isUpdating && isCreatingNewVersion) {
        // Create new version
        const currentVersion = parseFloat(formData.version);
        const newVersion = (Math.floor(currentVersion) + 0.1).toFixed(1);

        const newVersionPayload = {
          change_order_number: formData.change_order_number,
          version: newVersion,
          parent_change_order_id: formData.parent_change_order_id || formData.id,
          type: formData.type,
          line_items: processedLineItems,
          time_impact: parseFloat(formData.time_impact) || 0,
          cost_impact: parseFloat(formData.cost_impact) || 0,
          status: formData.status,
          approved_hours: parseFloat(formData.approved_hours) || 0,
          approved_cost: parseFloat(formData.approved_cost) || 0,
          approved_date: formData.status === "approved" ? (formData.approved_date || null) : null,
          branch: formData.branch || null,
          hyperlink: formData.hyperlink || null,
          remarks: formData.remarks || null
        };

        await axios.post(
          `${API}/projects/${project.id}/change-orders`,
          newVersionPayload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`New version ${newVersion} created!`);
      } else {
        // Create new change order
        const createPayload = {
          change_order_number: formData.change_order_number,
          version: "1.0",
          parent_change_order_id: null,
          type: formData.type,
          line_items: processedLineItems,
          time_impact: parseFloat(formData.time_impact) || 0,
          cost_impact: parseFloat(formData.cost_impact) || 0,
          status: formData.status,
          approved_hours: parseFloat(formData.approved_hours) || 0,
          approved_cost: parseFloat(formData.approved_cost) || 0,
          approved_date: formData.status === "approved" ? (formData.approved_date || null) : null,
          branch: formData.branch || null,
          hyperlink: formData.hyperlink || null,
          remarks: formData.remarks || null
        };

        await axios.post(
          `${API}/projects/${project.id}/change-orders`,
          createPayload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Change order added!");
      }

      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
      
      // Reset submit action
      submitActionRef.current = 'update';
    } catch (error) {
      console.error("Failed to save change order:", error);
      toast.error(error.response?.data?.detail || "Failed to save change order");
    }
  };

  const isLocked = formData.is_invoiced || formData.is_locked;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-md3-headline-small text-md3-on-surface font-semibold">
            {editMode ? "Edit Change Order" : "Add Change Order"}
            {formData.is_invoiced && (
              <Badge variant="outline" className="ml-2 border-orange-500 text-orange-700">
                <Lock className="h-3 w-3 mr-1" />
                Invoiced - Cannot Edit
              </Badge>
            )}
            {formData.is_locked && !formData.is_invoiced && (
              <Badge variant="outline" className="ml-2 border-orange-500 text-orange-700">
                <Lock className="h-3 w-3 mr-1" />
                Locked - Cannot Edit
              </Badge>
            )}
          </DialogTitle>
          {!editMode && (
            <DialogDescription className="text-md3-body-medium text-gray-600">Document project scope changes</DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Project Info (Read-only in Change Order Log context) */}
          {project && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-md3-md">
              <Label className="text-md3-label-medium text-gray-700 font-medium">Project</Label>
              <p className="font-medium text-md3-title-medium text-gray-900 mt-1">
                {project.project_number} - {project.name}
              </p>
            </div>
          )}

          {/* Change Order Number */}
          <div className="space-y-2">
            <Label htmlFor="co_number" className="text-md3-label-large text-gray-700 font-medium">Change Order Number (Format: CO#001) *</Label>
            <Input
              id="co_number"
              placeholder="CO#001"
              value={formData.change_order_number}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ ...formData, change_order_number: value });
                checkDuplicateCoNumber(value);
              }}
              required
              disabled={editMode || isLocked}
              className={`${duplicateWarning && !editMode ? "border-red-500" : ""} ${editMode || isLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
            />
            {editMode && (
              <p className="text-xs text-gray-600">
                🔒 Change order number cannot be edited once created
              </p>
            )}
            {!editMode && duplicateWarning && (
              <p className={`text-xs font-medium ${duplicateWarning.includes('Error') ? 'text-red-600' : 'text-orange-600'}`}>
                {duplicateWarning}
              </p>
            )}
            {!editMode && !duplicateWarning && (
              <p className="text-xs text-gray-500">Manual entry - must be unique within project</p>
            )}
          </div>

          {/* Version Info - Only in Edit Mode */}
          {editMode && (
            <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold text-blue-900">Current Version</Label>
                  <div className="text-lg font-bold text-blue-600">{formData.version}</div>
                </div>
                <Badge variant="outline" className="text-xs">
                  {formData.parent_change_order_id ? "Versioned" : "Original"}
                </Badge>
              </div>
              <p className="text-xs text-blue-700">
                Choose "Update" to modify this version or "Create New Version" to preserve this version and create {(Math.floor(parseFloat(formData.version)) + 0.1).toFixed(1)}
              </p>
            </div>
          )}

          {/* Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="co_type">Type *</Label>
            <Select 
              value={formData.type} 
              onValueChange={(value) => setFormData({ ...formData, type: value })}
              disabled={isLocked}
            >
              <SelectTrigger id="co_type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Detailing">Detailing</SelectItem>
                <SelectItem value="Engineering">Engineering</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">Applies to all line items in this change order</p>
          </div>

          {/* Line Items Section */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Line Items</Label>

            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
              {formData.line_items.map((lineItem, index) => (
                <div key={lineItem.id} className="border rounded-lg p-4 bg-gray-50 space-y-3">
                  <div className="flex justify-between items-center mb-2">
                    <Badge variant="outline" className="text-xs">Line Item {index + 1}</Badge>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeLineItem(lineItem.id)}
                      disabled={formData.line_items.length === 1 || isLocked}
                      className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md3-full transition-all duration-200"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Remove
                    </Button>
                  </div>

                  {/* Description */}
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">Description *</Label>
                    <Textarea
                      placeholder="Describe the change in detail..."
                      rows={3}
                      value={lineItem.description}
                      onChange={(e) => updateLineItem(lineItem.id, 'description', e.target.value)}
                      required
                      disabled={isLocked}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Reference Drawing</Label>
                      <Input
                        placeholder="e.g., DWG-001"
                        value={lineItem.reference_drawing}
                        onChange={(e) => updateLineItem(lineItem.id, 'reference_drawing', e.target.value)}
                        disabled={isLocked}
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Element Name</Label>
                      <Input
                        placeholder="e.g., PLATE, BEAM"
                        value={lineItem.element_name}
                        onChange={(e) => updateLineItem(lineItem.id, 'element_name', e.target.value)}
                        disabled={isLocked}
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Element Quantity</Label>
                      <Input
                        placeholder="e.g., 33"
                        value={lineItem.element_quantity}
                        onChange={(e) => updateLineItem(lineItem.id, 'element_quantity', e.target.value)}
                        disabled={isLocked}
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Action *</Label>
                      <Select
                        value={lineItem.action}
                        onValueChange={(value) => updateLineItem(lineItem.id, 'action', value)}
                        disabled={isLocked}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Addition">Addition</SelectItem>
                          <SelectItem value="Deletion">Deletion</SelectItem>
                          <SelectItem value="Modification">Modification</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Hours *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={lineItem.hours}
                        onChange={(e) => updateLineItem(lineItem.id, 'hours', e.target.value)}
                        required
                        disabled={isLocked}
                      />
                    </div>

                    {canViewCosts() && (
                      <div className="space-y-1">
                        <Label className="text-xs">Cost ($) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={lineItem.cost}
                          onChange={(e) => updateLineItem(lineItem.id, 'cost', e.target.value)}
                          required
                          disabled={isLocked}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Add Line Item Button */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addLineItem}
              disabled={isLocked}
              className="w-full border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
            >
              <Plus className="mr-1 h-4 w-4" /> Add Line Item
            </Button>
          </div>

          {/* Impact Summary */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="space-y-2">
              <Label htmlFor="co_time_impact" className="font-semibold">Time Impact (hours) *</Label>
              <Input
                id="co_time_impact"
                type="number"
                step="0.01"
                value={formData.time_impact}
                onChange={(e) => setFormData({ ...formData, time_impact: e.target.value })}
                required
                disabled={isLocked}
              />
              <p className="text-xs text-gray-600">Auto-calculated from line items (can be overridden)</p>
            </div>

            {canViewCosts() && (
              <div className="space-y-2">
                <Label htmlFor="co_cost_impact" className="font-semibold">Cost Impact ($) *</Label>
                <Input
                  id="co_cost_impact"
                  type="number"
                  step="0.01"
                  value={formData.cost_impact}
                  onChange={(e) => setFormData({ ...formData, cost_impact: e.target.value })}
                  required
                  disabled={isLocked}
                />
                <p className="text-xs text-gray-600">Auto-calculated from line items (can be overridden)</p>
              </div>
            )}
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="co_status">Status *</Label>
            <Select 
              value={formData.status} 
              onValueChange={(value) => setFormData({ ...formData, status: value })}
              disabled={isLocked}
            >
              <SelectTrigger id="co_status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">Changing to 'Approved' will update project totals</p>
          </div>

          {/* Approved Date/Cost/Hours - Only when status is approved (shown below status) */}
          {formData.status === 'approved' && (
            <div className="grid grid-cols-3 gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="space-y-2">
                <Label htmlFor="co_approved_date" className="font-semibold">Approved Date *</Label>
                <Input
                  id="co_approved_date"
                  type="date"
                  value={formData.approved_date || ""}
                  onChange={(e) => setFormData({ ...formData, approved_date: e.target.value })}
                  required
                  disabled={isLocked}
                  data-testid="co-approved-date-input"
                />
                <p className="text-xs text-gray-600">Date the CO was formally approved</p>
              </div>

              {canViewCosts() && (
                <div className="space-y-2">
                  <Label htmlFor="co_approved_cost" className="font-semibold">Approved Cost ($) *</Label>
                  <Input
                    id="co_approved_cost"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.approved_cost}
                    onChange={(e) => setFormData({ ...formData, approved_cost: e.target.value })}
                    required
                    disabled={isLocked}
                    data-testid="co-approved-cost-input"
                  />
                  <p className="text-xs text-gray-600">Added to allocated cost</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="co_approved_hours" className="font-semibold">Approved Hours *</Label>
                <Input
                  id="co_approved_hours"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.approved_hours}
                  onChange={(e) => setFormData({ ...formData, approved_hours: e.target.value })}
                  required
                  disabled={isLocked}
                  data-testid="co-approved-hours-input"
                />
                <p className="text-xs text-gray-600">Added to allocated hours (branch-specific)</p>
              </div>
            </div>
          )}

          {/* Branch Selection - Only for multi-branch projects */}
          {project && Array.isArray(project.branch) && project.branch.length > 1 && (
            <div className="space-y-2">
              <Label htmlFor="co_branch">Branch *</Label>
              <Select 
                value={formData.branch} 
                onValueChange={(value) => setFormData({ ...formData, branch: value })}
                disabled={isLocked}
              >
                <SelectTrigger id="co_branch">
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent>
                  {project.branch.map((branch) => (
                    <SelectItem key={branch} value={branch}>
                      {branch}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Hours will be added to this branch's allocation when approved</p>
            </div>
          )}

          {/* Work Completion Status — only active when CO is Approved */}
          {editMode && (
            <div className="space-y-2">
              <Label htmlFor="co_work_completion_status">Work Completion Status</Label>
              <Select
                value={formData.work_completion_status || "yet_to_start"}
                onValueChange={(value) => setFormData({ ...formData, work_completion_status: value })}
                disabled={(formData.status || "").toLowerCase() !== "approved"}
              >
                <SelectTrigger id="co_work_completion_status" data-testid="co-work-completion-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yet_to_start">Yet to Start</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting_for_rfi">Waiting for RFI</SelectItem>
                  <SelectItem value="released_for_approval">Released for Approval</SelectItem>
                  <SelectItem value="released_for_fab">Released for Fab</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {(formData.status || "").toLowerCase() === "approved"
                  ? "Project team can update this as work progresses on this change order."
                  : "Becomes editable once the change order status is Approved."}
              </p>
            </div>
          )}

          {/* Remarks */}
          <div className="space-y-2">
            <Label htmlFor="co_remarks">Remarks (Optional)</Label>
            <Textarea
              id="co_remarks"
              placeholder="Version-specific notes or comments..."
              rows={3}
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              disabled={isLocked}
            />
            <p className="text-xs text-gray-500">Add notes specific to this version</p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4 border-t">
            {editMode ? (
              <>
                <Button 
                  type="submit" 
                  variant="outline"
                  className="flex-1 border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                  disabled={isLocked}
                  onClick={() => {
                    submitActionRef.current = 'update';
                  }}
                >
                  Update Current Version
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                  disabled={isLocked}
                  onClick={() => {
                    submitActionRef.current = 'newVersion';
                  }}
                >
                  Create New Version {(Math.floor(parseFloat(formData.version)) + 0.1).toFixed(1)}
                </Button>
              </>
            ) : (
              <Button 
                type="submit" 
                className="w-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                disabled={duplicateWarning && duplicateWarning.includes('Error')}
              >
                Add Change Order
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
